import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  ContactPhone as ContactIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import apiClient from '../../../lib/api';

const AddFuneralCompanyPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: General
    companyName: '',
    description: '',
    serviceDescription: '',
    website: '',
    
    // Step 2: Contact
    contactPersonName: '',
    phone: '',
    email: '',
    
    // Step 3: Primary account (owner) – creates Stripe Connect + owner user
    ownerFirstName: '',
    ownerLastName: '',
    ownerEmail: '',
    ownerPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [stepValidation, setStepValidation] = useState([false, false, false, false]);

  const steps = [
    {
      label: 'General Information',
      icon: <BusinessIcon />,
      description: 'Company details and basic information'
    },
    {
      label: 'Brand',
      icon: <LanguageIcon />,
      description: 'Website'
    },
    {
      label: 'Contact Details',
      icon: <ContactIcon />,
      description: 'Company contact information'
    },
    {
      label: 'Primary account',
      icon: <PersonIcon />,
      description: 'Owner account (Stripe Connect will be created for payouts)'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Update validation when form data changes
  useEffect(() => {
    const validation = [
      // Step 0: General Information
      !!(formData.companyName.trim() && formData.description.trim()),
      // Step 1: Brand (optional - always valid)
      true,
      // Step 2: Contact Details
      !!(formData.contactPersonName.trim() && formData.phone.trim() && formData.email.trim()),
      // Step 3: Primary account (owner)
      !!(
        formData.ownerFirstName.trim() &&
        formData.ownerLastName.trim() &&
        formData.ownerEmail.trim()
      ),
    ];
    setStepValidation(validation);
  }, [formData]);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // General Information
      if (!formData.companyName.trim()) {
        newErrors.companyName = 'Company name is required';
      }
      if (!formData.description.trim()) {
        newErrors.description = 'Description is required';
      }
    } else if (step === 1) {
      // Brand (optional - no validation needed)
    } else if (step === 2) {
      // Contact Details
      if (!formData.contactPersonName.trim()) {
        newErrors.contactPersonName = 'Contact person name is required';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    } else if (step === 3) {
      // Primary account (owner)
      if (!formData.ownerFirstName.trim()) {
        newErrors.ownerFirstName = 'Owner first name is required';
      }
      if (!formData.ownerLastName.trim()) {
        newErrors.ownerLastName = 'Owner last name is required';
      }
      if (!formData.ownerEmail.trim()) {
        newErrors.ownerEmail = 'Owner email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.ownerEmail)) {
        newErrors.ownerEmail = 'Please enter a valid email address';
      }
      if (formData.ownerPassword && formData.ownerPassword.length < 8) {
        newErrors.ownerPassword = 'Password must be at least 8 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (stepIndex) => {
    // Only allow clicking on completed steps or the current step
    if (stepIndex !== activeStep) {
      setActiveStep(stepIndex);
    }
  };

  const copyContactToPrimaryAccount = () => {
    const nameTrimmed = (formData.contactPersonName || '').trim();
    const firstSpace = nameTrimmed.indexOf(' ');
    const ownerFirst = firstSpace > 0 ? nameTrimmed.slice(0, firstSpace) : nameTrimmed;
    const ownerLast = firstSpace > 0 ? nameTrimmed.slice(firstSpace + 1) : '';
    setFormData((prev) => ({
      ...prev,
      ownerFirstName: ownerFirst,
      ownerLastName: ownerLast,
      ownerEmail: (formData.email || '').trim(),
    }));
    if (ownerFirst || ownerLast || formData.email) {
      toast.success('Contact details copied to primary account');
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        companyName: formData.companyName,
        description: formData.description,
        contactPersonName: formData.contactPersonName,
        phone: formData.phone,
        email: formData.email,
        // Owner fields: trigger Stripe Connect + owner user creation
        ownerFirstName: formData.ownerFirstName.trim(),
        ownerLastName: formData.ownerLastName.trim(),
        ownerEmail: formData.ownerEmail.trim(),
        companyType: 'funeral',
      };

      if (formData.serviceDescription.trim()) {
        submitData.serviceDescription = formData.serviceDescription;
      }
      if (formData.website.trim()) {
        submitData.website = formData.website;
      }
      if (formData.ownerPassword.trim()) {
        submitData.ownerPassword = formData.ownerPassword;
      }

      const res = await apiClient.createFuneralCompany(submitData);

      if (res?.ownerPassword) {
        toast.success(
          `Company and owner created. Temporary password: ${res.ownerPassword}. Share it securely with the owner.`,
          { duration: 8000 }
        );
      } else {
        toast.success('Funeral Company and owner account created successfully!');
      }
      navigate('/companies');
    } catch (error) {
      console.error('Error creating funeral company:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create funeral company';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <TextField
              fullWidth
              label="Company Name"
              value={formData.companyName}
              onChange={(e) => handleInputChange('companyName', e.target.value)}
              error={!!errors.companyName}
              helperText={errors.companyName}
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              error={!!errors.description}
              helperText={errors.description}
              required
            />
            
            <TextField
              fullWidth
              label="Service Description"
              value={formData.serviceDescription}
              onChange={(e) => handleInputChange('serviceDescription', e.target.value)}
              placeholder="Describe the services offered..."
            />
          </Grid>
        );
        
      case 1:
        return (
          <Grid container spacing={3}>
            <TextField
              fullWidth
              label="Website"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder="https://example.com"
              type="url"
            />
          </Grid>
        );
        
      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Contact Person Name"
              value={formData.contactPersonName}
              onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
              error={!!errors.contactPersonName}
              helperText={errors.contactPersonName}
              required
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              error={!!errors.phone}
              helperText={errors.phone}
              required
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              This creates an owner account and a Stripe Connect account for the company so they can receive payouts.
            </Typography>
            <Box>
              <Button
                variant="outlined"
                size="small"
                onClick={copyContactToPrimaryAccount}
                disabled={!formData.contactPersonName?.trim() || !formData.email?.trim()}
              >
                Use contact details
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Copy contact person name and email from the Contact step
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Owner first name"
              value={formData.ownerFirstName}
              onChange={(e) => handleInputChange('ownerFirstName', e.target.value)}
              error={!!errors.ownerFirstName}
              helperText={errors.ownerFirstName}
              required
            />
            <TextField
              fullWidth
              label="Owner last name"
              value={formData.ownerLastName}
              onChange={(e) => handleInputChange('ownerLastName', e.target.value)}
              error={!!errors.ownerLastName}
              helperText={errors.ownerLastName}
              required
            />
            <TextField
              fullWidth
              label="Owner email"
              type="email"
              value={formData.ownerEmail}
              onChange={(e) => handleInputChange('ownerEmail', e.target.value)}
              error={!!errors.ownerEmail}
              helperText={errors.ownerEmail}
              placeholder="Used for login and Stripe Connect"
              required
            />
            <TextField
              fullWidth
              label="Owner password (optional)"
              type="password"
              value={formData.ownerPassword}
              onChange={(e) => handleInputChange('ownerPassword', e.target.value)}
              error={!!errors.ownerPassword}
              helperText={errors.ownerPassword || 'Leave blank to generate a temporary password'}
              inputProps={{ minLength: 8 }}
            />
          </Box>
        );
        
      default:
        return null;
    }
  };

  return (
    <DashboardLayout title="Add Funeral Company">
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            Add New Funeral Company
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Complete the form below to add a new funeral company to the system
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => step.icon}
                    optional={
                      <Typography variant="caption" color="text.secondary">
                        {step.description}
                      </Typography>
                    }
                    onClick={() => handleStepClick(index)}
                    sx={{ 
                      cursor: 'pointer !important',
                      color: !stepValidation[index] ? 'error.main' : 'inherit',
                      '& .MuiStepLabel-iconContainer': {
                        color: !stepValidation[index] ? 'error.main' : 'inherit',
                      },
                      '&:hover': index !== activeStep ? { 
                        opacity: 0.8,
                      } : {}
                    }}
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    {/* Back Button Above Content */}
                    {index > 0 && (
                      <Box sx={{ 
                        mb: 2, 
                        display: 'flex', 
                        justifyContent: 'center' 
                      }}>
                        <Button
                          onClick={handleBack}
                          startIcon={<KeyboardArrowUpIcon />}
                          variant="outlined"
                          fullWidth={{ xs: true, sm: false }}
                          sx={{ 
                            minWidth: { xs: '100%', sm: 200 }
                          }}
                        >
                          Back
                        </Button>
                      </Box>
                    )}
                    
                    {/* Divider between Back Button and Form */}
                    {index > 0 && (
                      <Box sx={{ 
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        mb: 3
                      }} />
                    )}
                    
                    {/* Form Content */}
                    <Box sx={{ mb: 3 }}>
                      {renderStepContent(index)}
                    </Box>
                    
                    {/* Next/Submit Button Below Content */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center',
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}>
                      {index === steps.length - 1 ? (
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={isLoading || !stepValidation.every(valid => valid)}
                          startIcon={isLoading ? null : <CheckIcon />}
                          fullWidth={{ xs: true, sm: false }}
                          sx={{ 
                            minWidth: { xs: '100%', sm: 200 }
                          }}
                        >
                          {isLoading ? 'Creating...' : 'Create Funeral Company'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          endIcon={<KeyboardArrowDownIcon />}
                          fullWidth={{ xs: true, sm: false }}
                          sx={{ 
                            minWidth: { xs: '100%', sm: 200 }
                          }}
                        >
                          Next
                        </Button>
                      )}
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
};

export default AddFuneralCompanyPage;
